import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import InfoTooltipSvg from '@/components/common/InfoTooltipSvg';
import Input from '@/components/common/input';
import { CartContext } from '@/contexts/CartContext';
import { TmCartPromocodeRequest } from '@/tscapi/model/request/cart-request';
import { AccountUtils } from '@/tscapi/utils/account-utils';
import { Cookie } from '@/tscapi/utils/cookie';

import { Popover, PopoverTrigger, PopoverContent, PopoverArrow, PopoverClose } from '../common/popover';

export const Promocode = () => {
    const { cartData, applyPromoCode, clearPromoCode, orderModificationMode, getModifiedOrderSummary } =
        useContext(CartContext);
    const [promoCode, setPromoCode] = useState('');
    const [esiCoupon, setEsiCoupon] = useState('');
    const [lastShipCode, setLastShipCode] = useState('');
    const [esiCouponValue, setEsiCouponValue] = useState('');
    const [editMode, setEditMode] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [isEmployeePromocodeEnabled, setIsEmployeePromocodeEnabled] = useState(false);
    const [isTouched, setIsTouched] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [cartPromocode, setCartPromocode] = useState('');
    const { t } = useTranslation();

    const cookie: Cookie = new Cookie();
    useEffect(() => {
        if (orderModificationMode) {
            if (cookie.ModifyOrderPromocode) {
                setCartPromocode(cookie.ModifyOrderPromocode);
            }
        } else if (cartData) {
            setCartPromocode(cartData.PromoCode);
            if (cartData.Buyer && cartData.Buyer.IsEmployee) {
                if (typeof window['employeePromocodeEnabled'] != 'undefined') {
                    setIsEmployeePromocodeEnabled(window['employeePromocodeEnabled']);
                }
            }
        }
        if (cartData?.ShipCode) {
            if (cartData?.ShipCode != lastShipCode) {
                //console.log('ship code changed, so clear the error message');
                setErrorMessage('');
                setLastShipCode(cartData.ShipCode);
            }
        }
    });
    const getESIPromoCode = () => {
        try {
            const goalMet = localStorage.getItem('__esi__ContributionGoalMet');
            if (goalMet && JSON.parse(goalMet).data == true) {
                let x = 0;
                const intervalID = setInterval(function () {
                    // keep checking for coupon code until it arrives or x seconds have passed
                    console.log('waiting for coupon ' + x);
                    if (++x === 40 || localStorage.getItem('__esi__coupon') !== null) {
                        window.clearInterval(intervalID);
                        const promoCodeData = JSON.parse(localStorage.getItem('__esi__coupon'));
                        if (promoCodeData) {
                            setEsiCoupon(promoCodeData.data?.coupon?.code);
                            setEsiCouponValue(promoCodeData.data?.coupon?.value);
                            setPromoCode(promoCodeData.data?.coupon?.code);
                        }
                    }
                }, 1000);
            }
        } catch (error) {
            console.log('Esi Coupon Error');
        }
    };
    useEffect(() => {
        getESIPromoCode();
    }, []);

    const onPromoChange = (e) => {
        setErrorMessage('');
        setPromoCode(e.target.value);
    };
    const editCartPromoCode = async () => {
        setPromoCode(cookie.ModifyOrderPromocode);
        setEditMode(true);
    };

    const applyCartPromoCode = async () => {
        if (orderModificationMode) {
            try {
                await getModifiedOrderSummary(cartData, promoCode.trim());
                if (promoCode) {
                    setEditMode(false);
                    cookie.ModifyOrderPromocode = promoCode.trim();
                } else {
                    setPromoCode('');
                    cookie.deleteModifyOrder_Promocode();
                }
            } catch (error) {
                setErrorMessage(
                    AccountUtils.getErrorMessage(error, t('Unable to apply this promo code. Please try again.')),
                );
            }
        } else {
            const promoCodeReq = new TmCartPromocodeRequest();
            promoCodeReq.Promocode = promoCode.trim();

            if (promoCode) {
                try {
                    await applyPromoCode(promoCodeReq);
                } catch (error) {
                    setErrorMessage(
                        AccountUtils.getErrorMessage(error, t('Unable to apply this promo code. Please try again.')),
                    );
                }
            }
        }
    };

    const clearCartPromoCode = async () => {
        setPromoCode('');
        setCartPromocode('');
        try {
            await clearPromoCode();
        } catch (error) {
            setErrorMessage(
                AccountUtils.getErrorMessage(error, t('Unable to remove this promo code. Please try again.')),
            );
        }
    };
    let timeOutClear;
    const handleEnter = () => {
        if (!isTouched) {
            clearTimeout(timeOutClear);
            setIsOpen(true);
        }
    };
    const handleLeave = () => {
        if (!isTouched) {
            timeOutClear = setTimeout(() => setIsOpen(false), 400);
        }
    };
    const handleTouchStart = () => {
        setIsOpen(true);
        setIsTouched(true);
    };
    const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
        // 'keypress' event misbehaves on mobile so we track 'Enter' key via 'keydown' event
        if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            applyCartPromoCode();
        }
    };

    return (
        <>
            {(!cartData.Buyer || (cartData.Buyer && (!cartData.Buyer.IsEmployee || isEmployeePromocodeEnabled))) && (
                <div className="promocode__container formWrap">
                    <h3 className="promocode__title">
                        {t('Enter Promo Code')}
                        <Popover placement="top">
                            <PopoverTrigger className="popover__button">
                                <span className="visually-hidden">{t('Enter Promo Code')}</span>
                                <InfoTooltipSvg className="popover__tooltip" />
                            </PopoverTrigger>
                            <PopoverContent className="popover__message">
                                {t('Only one promo code can be entered at a time.')}
                                <PopoverClose />
                                <PopoverArrow className="popover__arrow" />
                            </PopoverContent>
                        </Popover>
                    </h3>
                    {cartData && esiCoupon && (
                        <div className="promocode__esi-message">
                            Your promo code for ${esiCouponValue} off is {esiCoupon}
                        </div>
                    )}

                    <>
                        {cartData && cartPromocode && !editMode ? (
                            <>
                                <div className="promoremove__container promocode__applied">
                                    <div className="form__input promocode__code promocode__applied__code">
                                        {cartPromocode}
                                        {cartData.OrderSummary.PromocodeNotAppliedMessage ? (
                                            <div className="promocode__message--products">
                                                <div className="promocode__message-content">
                                                    <InfoTooltipSvg className="popover__tooltip-promocodeMessage" />
                                                    <span>{cartData.OrderSummary.PromocodeNotAppliedMessage}</span>
                                                </div>
                                            </div>
                                        ) : cartData.OrderSummary.PromocodeMessage ? (
                                            <div className="promocode__message--products">
                                                <span className="promocode__message">
                                                    {cartData.OrderSummary.PromocodeMessage}
                                                </span>
                                                {cartData.OrderSummary.PromotionDetails?.length > 0 && (
                                                    <div className="promotion_details">
                                                        {cartData.OrderSummary.PromotionDetails.map((promo, index) => (
                                                            <span key={index}>{promo}</span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ) : null}
                                    </div>
                                    {orderModificationMode ? (
                                        <button className="promocode__button--remove" onClick={editCartPromoCode}>
                                            {t('Edit')}
                                        </button>
                                    ) : (
                                        <button className="promocode__button--remove" onClick={clearCartPromoCode}>
                                            {t('Remove')}
                                        </button>
                                    )}
                                </div>
                            </>
                        ) : (
                            <>
                                <Input
                                    className="form__input promocode__input"
                                    id="promo"
                                    error={errorMessage}
                                    errorCSS={'form__error-svg form__error-svg--black'}
                                    name="giftcard"
                                    value={promoCode ? promoCode.toUpperCase() : ''}
                                    type="tel"
                                    onChange={onPromoChange}
                                    onKeyDown={onKeyDown}
                                />

                                <div className="promocode__spacer"></div>

                                <button
                                    className="promocode__button--white button"
                                    onClick={applyCartPromoCode}
                                    data-link-type="link"
                                    data-link-title="promo code"
                                    data-component-type="express checkout"
                                >
                                    {t('Apply')}
                                </button>
                            </>
                        )}
                    </>
                </div>
            )}
        </>
    );
};
